import {
	vultureENV,
} from 'Vulture/ENV';
import { s3PublicURL } from 'Vulture/S3.js';

const NO_SLASH_SYSTEM_LABELS = new Set([ 'Account Library', 'Stock Library', 'All Uploads', ]);
const UPLOADS_PREFIX = 'uploads/';

// True for the system-managed folder rows (Stock Library, Account Library,
// All Uploads, per-account Uploads). Mirrors backend `library.IsSystemFolder`:
// pairs label with the expected folder_path shape so user folders that share a
// label aren't false-positive.
export function isSystemFolder(file) {
	if (!file || file.isDir !== true) return false;
	const path = file.folder_path;
	if (typeof path !== 'string' || path.length === 0) return false;
	if (NO_SLASH_SYSTEM_LABELS.has(file.name)) return !path.includes('/');
	if (file.name === 'Uploads' && path.startsWith(UPLOADS_PREFIX)) {
		const rest = path.slice(UPLOADS_PREFIX.length);
		return rest.length > 0 && !rest.includes('/');
	}
	return false;
}

// Re-parents per-account Uploads folders under their matching Account Library
// for display only. Account Library and its Uploads share the same `name`
// (accountMD5); pairing is by that field. Backend/DB are unchanged.
export function nestUploadsUnderAccountLibrary(rootFolders) {
	if (!Array.isArray(rootFolders) || rootFolders.length === 0) return rootFolders;

	const uploadsByName = {};
	const remaining = [];
	rootFolders.forEach((node) => {
		if (node && node.label === 'Uploads' && node.name) {
			uploadsByName[node.name] = node;
		} else {
			remaining.push(node);
		}
	});

	if (Object.keys(uploadsByName).length === 0) return rootFolders;

	const result = remaining.map((node) => {
		if (node && node.label === 'Account Library' && uploadsByName[node.name]) {
			const uploadsNode = uploadsByName[node.name];
			delete uploadsByName[node.name];
			return {
				...node,
				sub_folders: [ ...(node.sub_folders || []), uploadsNode, ],
			};
		}
		return node;
	});

	// Orphan Uploads (no matching Account Library) stay at root so users don't lose access.
	Object.values(uploadsByName).forEach((node) => {
		return result.push(node);
	});

	return result;
}

export function transformFolderData(data) {
	const fileMap = {};

	fileMap['root'] = {
		id:          'root',
		name:        'Home',
		isDir:       true,
		parentId:    null,
		childrenIds: [],
	};

	function processNode(node, parentId) {
		const currentId = `node_${node.id}`;
		fileMap[currentId] = {
			id:          currentId,
			name:        node.label,
			folder_path: node.folder_path,
			isDir:       true,
			parentId,
			childrenIds: [],
		};

		if (parentId && fileMap[parentId]) {
			fileMap[parentId].childrenIds.push(currentId);
		}

		if (node.sub_folders && node.sub_folders.length > 0) {
			node.sub_folders.forEach((child) => {
				processNode(child, currentId);
			});
		}
	}

	data.forEach((node) => {
		processNode(node, 'root');
	});

	return fileMap;
}

export function transformFileData(currentFileMap, currentFolderId, filesData) {
	const newMap = {
		...currentFileMap,
	};

	const currentFolder = {
		...newMap[currentFolderId],
	};

	filesData.forEach((file) => {
		const fileId = `file_${file.id}`;

		if (!newMap[fileId]) {
			newMap[fileId] = {
				id:           fileId,
				name:         file.file_name || 'Unnamed file',
				size:         file.file_size || 0,
				parentId:     currentFolderId,
				isDir:        false,
				description:  file.description || '',
				keywords:     file.keywords || '',
				order:        file.order || '',
				resolution:   file.resolution || '',
				duration:     file.duration || '',
				path:         file.path || '',
				thumbnailUrl: s3PublicURL('ts-condor-assets', `${vultureENV}/ts-library/${file.path}`) || '',
			};
		}

		if (!currentFolder.childrenIds.includes(fileId)) {
			currentFolder.childrenIds = [
				...currentFolder.childrenIds, fileId,
			];
		}
	});

	newMap[currentFolderId] = currentFolder;
	return newMap;
}

export function transformSearchFileData(filesData) {
	const newArray = [];

	filesData.forEach((file) => {
		const fileId = `file_${file.id}`;
		newArray.push({
			id:           fileId,
			name:         file.file_name || 'Unnamed file',
			size:         file.file_size || 0,
			isDir:        false,
			description:  file.description || '',
			keywords:     file.keywords || '',
			resolution:   file.resolution || '',
			duration:     file.duration || '',
			path:         file.path || '',
			thumbnailUrl: s3PublicURL('ts-condor-assets', `${vultureENV}/ts-library/${file.path}`) || '',
		});
	});

	return newArray;
}

export function transformSearchFolderData(foldersData) {
	const newArray = [];

	foldersData.forEach((folder) => {
		newArray.push({
			id:          `node_${folder.id}`,
			name:        folder.label,
			folder_path: folder.folder_path,
			isDir:       true,
			childrenIds: folder.sub_folders || [],
		});
	});

	return newArray;
}
